import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, Input, input, output, viewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SafeUrl } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';

import { resolveValue } from '@lvflanders/bar';
import { resolveValue$ } from '@lvflanders/bar-ng';

import { _BarInteractiveElementBehavior } from '../interactive-element/bar-interactive-element-behavior';
import { _BarControlsHelper } from '../shared/bar-controls.helper';
import { BarLinkOptions } from './bar-link-options.model';

@Component({
    selector: 'bar-link',
    templateUrl: './bar-link.html',
    imports: [NgTemplateOutlet, NgClass, AsyncPipe, RouterModule, MatTooltipModule]
})
/** @deprecated Use BaasLink component within &lt;baas-layout&gt; with OnPush change detection. */
export class BarLink implements AfterViewInit {
    readonly controlsHelper = inject(_BarControlsHelper);
    readonly #clickableElementBehavior = inject(_BarInteractiveElementBehavior);

    @Input() item: BarLinkOptions;
    @Input() linkClass: string;
    readonly ommitCssClass = input<boolean>(undefined);
    // NOTE needed for ag grid cells with links to avoid losing the cell borders
    /** Whether to inject a zero whiteline if hidden. */
    readonly zeroWhiteLineIfHidden = input(false);
    readonly element = viewChild<ElementRef>('element');
    readonly clicked = output();

    get hidden() {
        return resolveValue(this.item.hidden || false);
    }

    get disabled() {
        return resolveValue(this.item.disabled || false);
    }

    get tooltip() {
        return resolveValue(this.item.title || '');
    }

    get tooltipClass() {
        return resolveValue(this.item.tooltipClass || '');
    }

    get content() {
        return resolveValue$(this.item.content);
    }

    get srText() {
        return resolveValue$(this.item.icon.srText);
    }

    get iconClass() {
        return resolveValue(this.item.icon.class);
    }

    get iconStyle() {
        return resolveValue(this.item.icon.style);
    }

    /**
    * performs the union of:
        - internal needed classes (disabled)
        - css classes needed for component layout
        - custom css classed defined by consumer
    */
    get ngClass(): any {
        const result = {};

        result['disabled'] = resolveValue(this.item.disabled);
        if (this.linkClass != null) {
            result[this.linkClass] = true;
        }

        if (this.ommitCssClass() !== true) {
            const cssClass = resolveValue(this.item.cssClass);
            if (cssClass) {
                result[cssClass] = true;
            }
        }

        return result;
    }

    get iconExists(): boolean {
        return !!this.item.icon;
    }

    get iconHasPosition(): boolean {
        return this.iconExists && !!this.item.icon?.position;
    }

    get iconHasLeftPosition(): boolean {
        return this.iconHasPosition && this.item.icon.position === 'left' ? true : false;
    }

    get iconHasRightPosition(): boolean {
        return this.iconHasPosition && this.item.icon.position === 'right' ? true : false;
    }

    get iconHasTopPosition(): boolean {
        return this.iconHasPosition && this.item.icon.position === 'top' ? true : false;
    }

    ngAfterViewInit() {
        if (this.item.handleElement != null) {
            // hook on native element
            this.item.handleElement(this.element().nativeElement);
        }
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled.set(isDisabled);
    }

    async linkClick(event): Promise<void> {
        await this.#clickableElementBehavior.linkClick(event, this.item);
        this.clicked.emit();
    }

    // invoked in case an action is defined and a routerLink
    async linkClickWithRouterLink(event): Promise<void> {
        await this.#clickableElementBehavior.linkClickWithRouterCommands(event, this.item);
    }

    executeHref(): string | SafeUrl {
        let href: string | SafeUrl = undefined;
        if (this.item.href) {
            href = resolveValue(this.item.href);
        } else if (this.item.routerUrl) {
            href = this.item.routerUrl;
        }

        return href;
    }
}
